import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBarcode,
    faPrint,
    faQrcode,
    faTags,
    faBoxes,
    faSlidersH,
    faRulerCombined,
    faCog,
    faTimes,
    faCheck,
    faExclamationTriangle,
    faSearch,
    faSpinner,
    faSave,
    faPlus,
    faMinus,
    faTrash,
    faEye,
    faCopy
} from '@fortawesome/free-solid-svg-icons';
import JsBarcode from 'jsbarcode';
import { useReactToPrint } from 'react-to-print';
import "../styles/Barcode.css";

// Barcode Printer Presets with standard roll printer sizes
const PRINTER_PRESETS = {
    'zebra_gk420': {
        name: 'Zebra GK420d',
        type: 'direct_thermal',
        width: 50.8, // mm (2 inches)
        height: 25.4, // mm (1 inch)
        dpi: 203,
        description: 'Direct Thermal Printer - 2" x 1" labels'
    },
    'zebra_zp450': {
        name: 'Zebra ZP 450',
        type: 'direct_thermal',
        width: 50.8,
        height: 25.4,
        dpi: 203,
        description: 'Direct Thermal Printer - 2" x 1" labels'
    },
    'zebra_zd420': {
        name: 'Zebra ZD420',
        type: 'thermal_transfer',
        width: 50.8,
        height: 25.4,
        dpi: 300,
        description: 'Thermal Transfer Printer - 2" x 1" high resolution'
    },
    'zebra_tlp2824': {
        name: 'Zebra TLP 2824',
        type: 'thermal_transfer',
        width: 50.8,
        height: 25.4,
        dpi: 203,
        description: 'Thermal Transfer Printer - Standard labels'
    },
    'datamax_m4210': {
        name: 'Datamax M-4210',
        type: 'thermal_transfer',
        width: 76.2, // 3 inches
        height: 25.4, // 1 inch
        dpi: 203,
        description: 'Wide format - 3" x 1" labels'
    },
    'godex_zt200': {
        name: 'Godex ZT200',
        type: 'thermal_transfer',
        width: 50.8,
        height: 25.4,
        dpi: 203,
        description: 'Industrial grade - 2" x 1" labels'
    },
    'intermec_pc43': {
        name: 'Intermec PC43',
        type: 'thermal_transfer',
        width: 50.8,
        height: 25.4,
        dpi: 300,
        description: 'High precision - 2" x 1" labels'
    },
    'custom_40x20': {
        name: 'Custom 40mm x 20mm',
        type: 'custom',
        width: 40,
        height: 20,
        dpi: 203,
        description: 'Small format labels'
    },
    'custom_50x30': {
        name: 'Custom 50mm x 30mm',
        type: 'custom',
        width: 50,
        height: 30,
        dpi: 203,
        description: 'Medium format labels'
    },
    'custom_75x50': {
        name: 'Custom 75mm x 50mm',
        type: 'custom',
        width: 75,
        height: 50,
        dpi: 203,
        description: 'Large format labels'
    },
    'custom_100x75': {
        name: 'Custom 100mm x 75mm',
        type: 'custom',
        width: 100,
        height: 75,
        dpi: 203,
        description: 'Extra large format labels'
    }
};

// Common roll paper sizes in mm
const ROLL_SIZES = [
    { width: 40, height: 20, name: '40mm × 20mm' },
    { width: 50, height: 25, name: '50mm × 25mm' },
    { width: 50, height: 30, name: '50mm × 30mm' },
    { width: 58, height: 40, name: '58mm × 40mm' },
    { width: 60, height: 40, name: '60mm × 40mm' },
    { width: 70, height: 40, name: '70mm × 40mm' },
    { width: 75, height: 50, name: '75mm × 50mm' },
    { width: 80, height: 50, name: '80mm × 50mm' },
    { width: 100, height: 75, name: '100mm × 75mm' }
];

const BarcodeGenerator = () => {
    const { user } = useAuth();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    
    // Barcode generation states
    const [barcodeType, setBarcodeType] = useState('CODE128');
    const [barcodeWidth, setBarcodeWidth] = useState(2);
    const [barcodeHeight, setBarcodeHeight] = useState(60);
    const [showText, setShowText] = useState(true);
    const [fontSize, setFontSize] = useState(12);
    const [margin, setMargin] = useState(5);
    
    // Printer and label settings
    const [selectedPrinter, setSelectedPrinter] = useState('zebra_gk420');
    const [customWidth, setCustomWidth] = useState(50);
    const [customHeight, setCustomHeight] = useState(25);
    const [labelsPerRow, setLabelsPerRow] = useState(1);
    const [labelSpacing, setLabelSpacing] = useState(2);
    
    // Print quantity settings
    const [printMode, setPrintMode] = useState('custom'); // 'stock' or 'custom'
    const [customQuantity, setCustomQuantity] = useState(1);
    const [printCopies, setPrintCopies] = useState(1);
    
    // Preview and print states
    const [previewItems, setPreviewItems] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const [showToast, setShowToast] = useState({ show: false, type: '', message: '' });
    
    const printRef = useRef();

    // Fetch products on mount
    useEffect(() => {
        if (user && user.username) {
            fetchProducts();
        } else {
            setLoading(false);
            showNotification('error', 'User not authenticated. Please login again.');
        }
    }, [user]);

    const fetchProducts = async () => {
        if (!user || !user.username) return;

        const currentShopId = localStorage.getItem('current_shop_id') || user.shop_id;

        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/products.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user.username,
                    shop_id: currentShopId,
                    filters: { username: user.username }
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setProducts(data.data);
                setFilteredProducts(data.data);
            } else {
                showNotification('error', data.message || 'Failed to fetch products');
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            showNotification('error', 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const showNotification = (type, message) => {
        setShowToast({ show: true, type, message });
        setTimeout(() => setShowToast({ show: false, type: '', message: '' }), 3000);
    };

    // Filter products based on search
    useEffect(() => {
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            const filtered = products.filter(product =>
                product.product_name?.toLowerCase().includes(term) ||
                product.batch_no?.toLowerCase().includes(term) ||
                product.barcode?.toLowerCase().includes(term)
            );
            setFilteredProducts(filtered);
        } else {
            setFilteredProducts(products);
        }
    }, [searchTerm, products]);

    // Handle product selection
    const toggleProductSelection = (product) => {
        setSelectedProducts(prev => {
            const exists = prev.find(p => p.id === product.id);
            if (exists) {
                return prev.filter(p => p.id !== product.id);
            } else {
                return [...prev, { ...product, quantity: 1 }];
            }
        });
    };

    const selectAllProducts = () => {
        setSelectedProducts(filteredProducts.map(p => ({ ...p, quantity: 1 })));
    };

    const clearSelection = () => {
        setSelectedProducts([]);
    };

    // Update selected product quantity
    const updateSelectedQuantity = (productId, newQuantity) => {
        setSelectedProducts(prev =>
            prev.map(p =>
                p.id === productId
                    ? { ...p, quantity: Math.max(1, newQuantity) }
                    : p
            )
        );
    };

    const removeSelectedProduct = (productId) => {
        setSelectedProducts(prev => prev.filter(p => p.id !== productId));
    };

    // Generate preview items based on print mode
    const generatePreview = () => {
        if (selectedProducts.length === 0) {
            showNotification('error', 'Please select at least one product');
            return;
        }

        const items = [];
        
        selectedProducts.forEach(product => {
            let quantityToGenerate = 0;
            
            if (printMode === 'stock') {
                // Generate barcodes for all existing stock
                quantityToGenerate = parseInt(product.quantity) || 0;
            } else {
                // Custom quantity per product
                quantityToGenerate = product.quantity;
            }

            // Multiply by print copies
            const totalQuantity = quantityToGenerate * printCopies;

            for (let i = 0; i < totalQuantity; i++) {
                items.push({
                    id: `${product.id}_${i}`,
                    product: product,
                    barcode: product.barcode,
                    productName: product.product_name,
                    batchNo: product.batch_no,
                    price: product.sell_price
                });
            }
        });

        setPreviewItems(items);
        setShowPreview(true);
    };

    // Get current printer settings
    const getPrinterSettings = () => {
        const preset = PRINTER_PRESETS[selectedPrinter] || PRINTER_PRESETS.zebra_gk420;
        
        // Convert mm to pixels for printing (1mm ≈ 3.78px at 96dpi, but we use dpi-based conversion)
        const widthPx = Math.round((preset.width / 25.4) * preset.dpi);
        const heightPx = Math.round((preset.height / 25.4) * preset.dpi);
        
        return {
            ...preset,
            widthPx,
            heightPx,
            labelWidth: preset.width,
            labelHeight: preset.height
        };
    };

    // Handle print
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        onBeforeGetContent: () => {
            setIsPrinting(true);
            return Promise.resolve();
        },
        onAfterPrint: () => {
            setIsPrinting(false);
            setShowPreview(false);
            showNotification('success', `Printing ${previewItems.length} barcode labels`);
        },
        onPrintError: (error) => {
            setIsPrinting(false);
            showNotification('error', 'Print failed: ' + error.message);
        }
    });

    // Generate barcode SVG for a single item
    const generateBarcodeSVG = (item, index) => {
        const printer = getPrinterSettings();
        
        return (
            <div
                key={item.id}
                className="barcode-item"
                style={{
                    width: `${printer.width}mm`,
                    height: `${printer.height}mm`,
                    margin: `${labelSpacing}mm`,
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed #ccc',
                    padding: '2mm',
                    boxSizing: 'border-box',
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid'
                }}
            >
                <svg
                    ref={el => {
                        if (el) {
                            try {
                                JsBarcode(el, item.barcode, {
                                    format: barcodeType,
                                    width: barcodeWidth,
                                    height: barcodeHeight,
                                    displayValue: showText,
                                    fontSize: fontSize,
                                    margin: margin,
                                    text: showText ? `${item.productName} - ${item.batchNo}` : undefined
                                });
                            } catch (err) {
                                console.error('Barcode generation error:', err);
                            }
                        }
                    }}
                />
                {showText && (
                    <div className="barcode-info" style={{ fontSize: '8pt', textAlign: 'center' }}>
                        <div>{item.productName}</div>
                        <div>Batch: {item.batchNo}</div>
                        {item.price && <div>Price: Rs. {item.price}</div>}
                        <div style={{ fontSize: '7pt', color: '#666' }}>#{item.barcode}</div>
                    </div>
                )}
            </div>
        );
    };

    const isRTL = localStorage.getItem('ourzest_language') === 'ur' || localStorage.getItem('ourzest_language') === 'ar';

    return (
        <div className={`barcode-generator-container ${isRTL ? 'rtl' : ''}`}>
            {/* Toast Notification */}
            {showToast.show && (
                <div className={`toast-notification ${showToast.type}`}>
                    <FontAwesomeIcon icon={showToast.type === 'error' ? faExclamationTriangle : faCheck} />
                    <span>{showToast.message}</span>
                </div>
            )}

          {/* Header */}
<div className="form-header-modern">
    <div className="header-content">
        <div className="header-icon">
            <FontAwesomeIcon icon={faBarcode} />
        </div>
        <div className="header-text">
            <h1>Barcode Generator</h1>
            <p>Generate and print barcodes for your products</p>
        </div>
    </div>
</div>

            {/* Main Content - Two Column Layout */}
            <div className="barcode-main-content">
                {/* Left Column - Product Selection */}
                <div className="barcode-left-panel">
                    <div className="panel-header">
                        <h3>
                            <FontAwesomeIcon icon={faBoxes} />
                            Select Products
                        </h3>
                        <div className="panel-actions">
                            <button onClick={selectAllProducts} className="btn-small">
                                Select All
                            </button>
                            <button onClick={clearSelection} className="btn-small">
                                Clear
                            </button>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="search-box">
                        <FontAwesomeIcon icon={faSearch} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search products by name, batch, or barcode..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Products List */}
                    <div className="products-list">
                        {loading ? (
                            <div className="loading-container">
                                <div className="loading-spinner"></div>
                                <p>Loading products...</p>
                                <p className="loading-subtext">Please wait while we fetch your products</p>
                            </div>
                        ) : filteredProducts.length === 0 ? (
                            <div className="no-products">
                                <p>No products found</p>
                            </div>
                        ) : (
                            filteredProducts.map(product => {
                                const isSelected = selectedProducts.some(p => p.id === product.id);
                                return (
                                    <div
                                        key={product.id}
                                        className={`product-item ${isSelected ? 'selected' : ''}`}
                                        onClick={() => toggleProductSelection(product)}
                                    >
                                        <div className="product-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => toggleProductSelection(product)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <div className="product-info">
                                            <div className="product-name">{product.product_name}</div>
                                            <div className="product-details">
                                                <span className="batch">Batch: {product.batch_no}</span>
                                                <span className="barcode">#{product.barcode}</span>
                                            </div>
                                            <div className="product-stats">
                                                <span className="stock">Stock: {product.quantity}</span>
                                                <span className="price">Rs. {product.sell_price}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Right Column - Settings and Selected Products */}
                <div className="barcode-right-panel">
                    {/* Selected Products */}
                    <div className="selected-products-section">
                        <h3>
                            <FontAwesomeIcon icon={faTags} />
                            Selected Products ({selectedProducts.length})
                        </h3>
                        
                        {selectedProducts.length === 0 ? (
                            <div className="no-selection">
                                <p>No products selected</p>
                                <small>Select products from the left panel</small>
                            </div>
                        ) : (
                            <div className="selected-items-list">
                                {selectedProducts.map(product => (
                                    <div key={product.id} className="selected-item">
                                        <div className="item-info">
                                            <div className="item-name">{product.product_name}</div>
                                            <div className="item-batch">Batch: {product.batch_no}</div>
                                        </div>
                                        <div className="item-controls">
                                            <div className="quantity-control">
                                                <button
                                                    onClick={() => updateSelectedQuantity(product.id, product.quantity - 1)}
                                                    disabled={product.quantity <= 1}
                                                >
                                                    <FontAwesomeIcon icon={faMinus} size="xs" />
                                                </button>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={product.quantity}
                                                    onChange={(e) => updateSelectedQuantity(product.id, parseInt(e.target.value) || 1)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <button
                                                    onClick={() => updateSelectedQuantity(product.id, product.quantity + 1)}
                                                >
                                                    <FontAwesomeIcon icon={faPlus} size="xs" />
                                                </button>
                                            </div>
                                            <button
                                                className="remove-btn"
                                                onClick={() => removeSelectedProduct(product.id)}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Barcode Settings */}
                    <div className="settings-section">
                        <h3>
                            <FontAwesomeIcon icon={faCog} />
                            Barcode Settings
                        </h3>
                        
                        <div className="settings-grid">
                            <div className="setting-item">
                                <label>Barcode Type</label>
                                <select value={barcodeType} onChange={(e) => setBarcodeType(e.target.value)}>
                                    <option value="CODE128">CODE128 (Standard)</option>
                                    <option value="CODE39">CODE39</option>
                                    <option value="EAN13">EAN13</option>
                                    <option value="EAN8">EAN8</option>
                                    <option value="UPC">UPC</option>
                                    <option value="ITF14">ITF14</option>
                                    <option value="MSI">MSI</option>
                                    <option value="pharmacode">Pharmacode</option>
                                </select>
                            </div>

                            <div className="setting-item">
                                <label>Barcode Width</label>
                                <input
                                    type="range"
                                    min="1"
                                    max="4"
                                    step="0.1"
                                    value={barcodeWidth}
                                    onChange={(e) => setBarcodeWidth(parseFloat(e.target.value))}
                                />
                                <span className="value-display">{barcodeWidth.toFixed(1)}px</span>
                            </div>

                            <div className="setting-item">
                                <label>Barcode Height</label>
                                <input
                                    type="range"
                                    min="30"
                                    max="120"
                                    value={barcodeHeight}
                                    onChange={(e) => setBarcodeHeight(parseInt(e.target.value))}
                                />
                                <span className="value-display">{barcodeHeight}px</span>
                            </div>

                            <div className="setting-item checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={showText}
                                        onChange={(e) => setShowText(e.target.checked)}
                                    />
                                    Show Text
                                </label>
                            </div>

                            {showText && (
                                <>
                                    <div className="setting-item">
                                        <label>Font Size</label>
                                        <input
                                            type="range"
                                            min="8"
                                            max="20"
                                            value={fontSize}
                                            onChange={(e) => setFontSize(parseInt(e.target.value))}
                                        />
                                        <span className="value-display">{fontSize}pt</span>
                                    </div>

                                    <div className="setting-item">
                                        <label>Margin</label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="20"
                                            value={margin}
                                            onChange={(e) => setMargin(parseInt(e.target.value))}
                                        />
                                        <span className="value-display">{margin}px</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Printer Settings */}
                    <div className="settings-section">
                        <h3>
                            <FontAwesomeIcon icon={faPrint} />
                            Printer Settings
                        </h3>

                        <div className="settings-grid">
                            <div className="setting-item full-width">
                                <label>Printer Preset</label>
                                <select
                                    value={selectedPrinter}
                                    onChange={(e) => setSelectedPrinter(e.target.value)}
                                >
                                    {Object.entries(PRINTER_PRESETS).map(([key, preset]) => (
                                        <option key={key} value={key}>
                                            {preset.name} - {preset.description}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {selectedPrinter.startsWith('custom') && (
                                <>
                                    <div className="setting-item">
                                        <label>Label Width (mm)</label>
                                        <input
                                            type="number"
                                            min="20"
                                            max="150"
                                            value={customWidth}
                                            onChange={(e) => setCustomWidth(parseInt(e.target.value))}
                                        />
                                    </div>

                                    <div className="setting-item">
                                        <label>Label Height (mm)</label>
                                        <input
                                            type="number"
                                            min="10"
                                            max="150"
                                            value={customHeight}
                                            onChange={(e) => setCustomHeight(parseInt(e.target.value))}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="setting-item">
                                <label>Labels Per Row</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="5"
                                    value={labelsPerRow}
                                    onChange={(e) => setLabelsPerRow(parseInt(e.target.value))}
                                />
                            </div>

                            <div className="setting-item">
                                <label>Label Spacing (mm)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="10"
                                    step="0.5"
                                    value={labelSpacing}
                                    onChange={(e) => setLabelSpacing(parseFloat(e.target.value))}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Print Settings */}
                    <div className="settings-section">
                        <h3>
                            <FontAwesomeIcon icon={faSlidersH} />
                            Print Settings
                        </h3>

                        <div className="print-mode-selector">
                            <label className={`mode-option ${printMode === 'stock' ? 'active' : ''}`}>
                                <input
                                    type="radio"
                                    name="printMode"
                                    value="stock"
                                    checked={printMode === 'stock'}
                                    onChange={(e) => setPrintMode(e.target.value)}
                                />
                                <div className="mode-content">
                                    <span className="mode-title">Stock Quantity</span>
                                    <small>Print barcodes for all available stock</small>
                                </div>
                            </label>

                            <label className={`mode-option ${printMode === 'custom' ? 'active' : ''}`}>
                                <input
                                    type="radio"
                                    name="printMode"
                                    value="custom"
                                    checked={printMode === 'custom'}
                                    onChange={(e) => setPrintMode(e.target.value)}
                                />
                                <div className="mode-content">
                                    <span className="mode-title">Custom Quantity</span>
                                    <small>Print specific number per product</small>
                                </div>
                            </label>
                        </div>

                        <div className="print-copies">
                            <label>Print Copies (Sets)</label>
                            <div className="copies-control">
                                <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={printCopies}
                                    onChange={(e) => setPrintCopies(parseInt(e.target.value) || 1)}
                                />
                                <span className="copies-hint">
                                    Total labels: {selectedProducts.reduce((sum, p) => {
                                        const qty = printMode === 'stock' ? parseInt(p.quantity) || 0 : p.quantity;
                                        return sum + (qty * printCopies);
                                    }, 0)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="action-buttons">
                        <button
                            className="btn-preview"
                            onClick={generatePreview}
                            disabled={selectedProducts.length === 0}
                        >
                            <FontAwesomeIcon icon={faEye} />
                            Preview Barcodes
                        </button>
                        <button
                            className="btn-print"
                            onClick={handlePrint}
                            disabled={previewItems.length === 0 || isPrinting}
                        >
                            {isPrinting ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                    Printing...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faPrint} />
                                    Print Barcodes ({previewItems.length})
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Preview Modal */}
            {showPreview && (
                <div className="preview-modal" onClick={() => setShowPreview(false)}>
                    <div className="preview-content" onClick={(e) => e.stopPropagation()}>
                        <div className="preview-header">
                            <h3>
                                <FontAwesomeIcon icon={faEye} />
                                Barcode Preview
                            </h3>
                            <button className="close-btn" onClick={() => setShowPreview(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        
                        <div className="preview-body">
                            <div className="preview-info">
                                <span>Total Labels: {previewItems.length}</span>
                                <span>Printer: {PRINTER_PRESETS[selectedPrinter].name}</span>
                                <span>Label Size: {PRINTER_PRESETS[selectedPrinter].width}mm × {PRINTER_PRESETS[selectedPrinter].height}mm</span>
                            </div>
                            
                            <div
                                ref={printRef}
                                className="barcode-preview-grid"
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    justifyContent: 'flex-start',
                                    alignItems: 'flex-start'
                                }}
                            >
                                {previewItems.map((item, index) => generateBarcodeSVG(item, index))}
                            </div>
                        </div>
                        
                        <div className="preview-footer">
                            <button className="btn-secondary" onClick={() => setShowPreview(false)}>
                                Cancel
                            </button>
                            <button className="btn-primary" onClick={handlePrint} disabled={isPrinting}>
                                {isPrinting ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                        Printing...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faPrint} />
                                        Print Now
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BarcodeGenerator;